import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getDashboardRoles, getDefaultDashboard } from '../components/DashboardSelector';
import { tokenStorage } from '../lib/tokenStorage';

const AuthContext = createContext({});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(tokenStorage.getToken());
  const [selectedDashboard, setSelectedDashboard] = useState(tokenStorage.getDashboard());

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = tokenStorage.getToken();
      if (storedToken) {
        setToken(storedToken);
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` },
          });
          setUser(response.data);
          
          const userData = response.data;
          const storedDashboard = tokenStorage.getDashboard();
          const dashboardOptions = getDashboardRoles(userData?.roles);
          
          if (userData?.roles?.includes('admin')) {
            setSelectedDashboard(null);
          } else if (dashboardOptions.length === 1) {
            setSelectedDashboard(dashboardOptions[0].value);
          } else if (storedDashboard && dashboardOptions.some(o => o.value === storedDashboard)) {
            setSelectedDashboard(storedDashboard);
          } else if (dashboardOptions.length > 0) {
            setSelectedDashboard(dashboardOptions[0].value);
          }
        } catch (error) {
          console.error('Failed to fetch user:', error);
          tokenStorage.removeToken();
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    tokenStorage.setToken(access_token);
    setToken(access_token);
    setUser(userData);
    
    const dashboardOptions = getDashboardRoles(userData?.roles);
    if (!userData?.roles?.includes('admin') && dashboardOptions.length > 0) {
      const storedDashboard = tokenStorage.getDashboard();
      if (storedDashboard && dashboardOptions.some(o => o.value === storedDashboard)) {
        setSelectedDashboard(storedDashboard);
      } else {
        setSelectedDashboard(dashboardOptions[0].value);
      }
    }
    
    return userData;
  };

  const setUserFromOAuth = (accessToken, userData) => {
    tokenStorage.setToken(accessToken);
    setToken(accessToken);
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout API error:', error);
    }
    tokenStorage.clearAll();
    setToken(null);
    setUser(null);
    setSelectedDashboard(null);
  };

  const changeDashboard = (dashboardType) => {
    setSelectedDashboard(dashboardType);
    tokenStorage.setDashboard(dashboardType);
  };

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
  }), [token]);

  return (
    <AuthContext.Provider
      value={{ 
        user, 
        loading, 
        login, 
        logout, 
        getAuthHeaders, 
        token, 
        setUserFromOAuth,
        selectedDashboard,
        changeDashboard,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
